import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';

import { 
  ArrowLeft, 
  User, 
  FileText, 
  Building, 
  Calendar, 
  RefreshCw,
  Save,
  X,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  GraduationCap,
  Shield,
  Users,
  DollarSign,
  AlertCircle,
  Check,
  CheckCircle,
  XCircle,
  Info
} from 'lucide-react';
import { apiClient } from '../../lib/api/apiClient';
import { useEmployeeStream } from '../../hooks/EmployeStream';
import ToastContainer from '../../components/ui/ToastContainer';
import { useAuth } from '../../context/AuthContext';
// import ToastContainer from '../../components/ui/ToastContainer';

const EmployeeEditPage = () => {
  const { employeeCode } = useParams();
  const [employeeData, setEmployeeData] = useState({
    eid: "",
    name: "",
    firstName: "",
    middleName: "",
    lastName: "",
    isVerified: false,
    personal_information: {
      employee_name: "",
      father_name: "",
      mother_name: "",
      date_of_birth: "",
      place_of_birth: "",
      gender: "",
      nationality: "",
      religion: "",
      marital_status: "",
      blood_group: "",
      languages_known: []
    },
    address_information: {
      permanent_address: {
        village: "",
        post: "",
        district: "",
        state: "",
        pin_code: ""
      }
    },
    contact_information: {
      phone_number: "",
      email: "",
      emergency_contact: {
        name: "",
        mobile: "",
        email: ""
      }
    },
    identification_documents: {
      aadhaar_number: "",
      pan_number: ""
    },
    employment_details: {
      designation: "",
      grade: "",
      eid: "",
      uan_number: "",
      date_of_joining: "",
      date_of_appointment: "",
      location: "",
      division: "",
      reporting_manager: "",
      source_of_candidate: ""
    },
    salary_and_compensation: {
      ctc_annual: 0,
      basic_pay: {
        monthly: 0
      },
      house_rent: {
        monthly: 0
      },
      special_allowance: {
        monthly: 0
      },
      performance_allowance: {
        monthly: 0
      },
      bonus_exgratia: {
        monthly: 0
      },
      pf_employer_contribution: {
        monthly: 0
      }
    },
    bank_details: {
      bank_name: "",
      account_number: "",
      ifsc_code: ""
    },
    nominee_information: [],
    declarations_and_compliance: {
      physical_disabilities: "",
      major_illness: "",
      court_cases: "",
      police_custody: ""
    },
    education: {
      higher_education: ""
    }
  });
  const [oldEmployeeData, setOldEmployeeData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newLanguage, setNewLanguage] = useState('');

  function findUpdatedFields(oldData, newData, prefix = "") {
    let updates = {};
  
    for (const key in newData) {
      const newValue = newData[key];
      const oldValue = oldData ? oldData[key] : undefined;
      const path = prefix ? `${prefix}.${key}` : key;
  
      if (Array.isArray(newValue)) {
        if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
          updates[path] = { oldValue, newValue };
        }
      } else if (typeof newValue === "object" && newValue !== null) {
        Object.assign(updates, findUpdatedFields(oldValue, newValue, path));
      } else {
        if (newValue !== oldValue) {
          updates[path] = { oldValue, newValue };
        }
      }
    }
  
    return updates;
  }
  

  // Simulate fetching employee data (you would replace this with actual API call)
  useEffect(() => {
    fetchEmployeeData();
  }, []);

  // update employee data when change isActive true or false in true keep current data once it become false add inactive_info group to employeeData and once it became again true then remove this group
  useEffect(() => {
    if (employeeData.isActive) {
      const {inactive_info, ...rest} = employeeData;
      setEmployeeData(rest);
    }else{
      if(!employeeData.inactive_info){
        setEmployeeData({...employeeData, inactive_info: {
          last_working_day: new Date().toISOString().split("T")[0],
          resignation_date: new Date().toISOString().split("T")[0],
          reason_for_leaving: "",
          remarks: ""
      }});
      }
    }
  }, [employeeData.isActive]);
  
  const splitName = (full_name) => {
    // Trim spaces and split by one or more spaces
    const nameParts = full_name.trim().split(/\s+/);
  
    let firstName = "";
    let middleName = "";
    let lastName = "";
  
    if (nameParts.length === 1) {
      // Only first name
      firstName = nameParts[0];
    } else if (nameParts.length === 2) {
      // First + last name
      firstName = nameParts[0];
      lastName = nameParts[1];
    } else if (nameParts.length >= 3) {
      // First + middle (all except first and last) + last
      firstName = nameParts[0];
      lastName = nameParts[nameParts.length - 1];
      middleName = nameParts.slice(1, -1).join(" ");
    }
  
    return { firstName, middleName, lastName };
  };
  const fetchEmployeeData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/v1/employees/${employeeCode}`);
  
      const full_name = response.personal_information.employee_name;
      const { firstName, middleName, lastName } = splitName(full_name);
  
      setEmployeeData({ firstName, middleName, lastName, ...response });
  
      // Deep copy to avoid reference issues
      setOldEmployeeData(cloneDeep(response));
  
    } catch (error) {
      console.error("Error fetching employee data:", error);
    } finally {
      setLoading(false);
    }
  };

  function formatToISO(dateStr) {
    // dd/mm/yyyy → yyyy-mm-dd
    const [day, month, year] = dateStr.split("/");
    return `${year}-${month}-${day}`;
  }
  
  function formatToDisplay(dateStr) {
    // yyyy-mm-dd → dd/mm/yyyy
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  }

  const handleInputChange = (path, value) => {
    const keys = path.split('.');
    setEmployeeData(prev => {
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const addLanguage = () => {
    if (newLanguage.trim()) {
      setEmployeeData(prev => ({
        ...prev,
        personal_information: {
          ...prev.personal_information,
          languages_known: [...prev.personal_information.languages_known, newLanguage.trim()]
        }
      }));
      setNewLanguage('');
    }
  };

  const removeLanguage = (index) => {
    setEmployeeData(prev => ({
      ...prev,
      personal_information: {
        ...prev.personal_information,
        languages_known: prev.personal_information.languages_known.filter((_, i) => i !== index)
      }
    }));
  };

  const addNominee = () => {
    setEmployeeData(prev => ({
      ...prev,
      nominee_information: [
        ...prev.nominee_information,
        {
          nominee_name: "",
          relationship: "",
          nominee_age: "",
          nominee_dob: "",
          share_percentage: ""
        }
      ]
    }));
  };

  const removeNominee = (index) => {
    setEmployeeData(prev => ({
      ...prev,
      nominee_information: prev.nominee_information.filter((_, i) => i !== index)
    }));
  };

  const handleNomineeChange = (index, field, value) => {
    setEmployeeData(prev => ({
      ...prev,
      nominee_information: prev.nominee_information.map((nominee, i) => 
        i === index ? { ...nominee, [field]: value } : nominee
      )
    }));
  };
  const [toasts, setToasts] = useState([]);
  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
    // Toast management
    const showToast = (message, type = 'info') => {
      const id = Date.now();
      const toast = { id, message, type };
      setToasts(prev => [...prev, toast]);
      
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, 5000);
    };
  
  const { updateEmployee } = useEmployeeStream(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/employees/stream`);
  const {user} = useAuth();
  const handleSave = async () => {
    setSaving(true);
    try {
      const { firstName, middleName, lastName, ...rest } = employeeData;
      if (Object.keys(findUpdatedFields(oldEmployeeData, rest)).length === 0) {
        showToast('No changes detected', 'info');
        return;
      }
      const full_name = `${firstName} ${middleName} ${lastName}`;
      rest.personal_information.employee_name = full_name;
      rest.name = full_name;
      let payload = rest;
      if (user.right==="EMPLOYEE"){
        payload = {
          eid: user.eid,
          isDocument: false,
          fieldsUpdated: findUpdatedFields(oldEmployeeData, rest),
          data: rest
        }
      }
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/${employeeData.eid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify(payload),
      });
      updateEmployee(rest);      
      if (!response.ok) {
        const errorData = await response.json();
        showToast("Error: " + errorData.detail, 'error');
        return;
      }
      const data = await response.json();
      showToast(data.message, 'success');
      // Simulate API delay
    } catch (error) {
      console.error('Error saving employee data:', error);
    } finally {
      setSaving(false);
    }
  };

  // Helper function: Capitalize first letter of every word
  function toTitleCase(str) {
    if (!str) return "";
    return str
      .toString()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
  const SelectField = ({ label, value, onChange, options, required = false }) => (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="">Select {label}</option>
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </div>
  );

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Loading employee data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-6">
          <button 
            onClick={() => window.history.back()}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
            title="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Edit Employee</h1>
            <p className="text-sm sm:text-base text-gray-600">Update employee information</p>
          </div>
        </div>

        {/* Employee Info Card */}
        <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              <div className="p-3 bg-blue-100 rounded-full self-start sm:self-auto">
                <User className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">{toTitleCase(employeeData.personal_information.employee_name)}</h2>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 mt-2 text-gray-600">
                  <div className="flex items-center gap-1">
                    <FileText className="h-4 w-4" />
                    <span className="text-sm sm:text-base">ID: {employeeData.eid}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Building className="h-4 w-4" />
                    <span className="text-sm sm:text-base">{toTitleCase(employeeData.employment_details.division)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span className="text-sm sm:text-base">Joined: {employeeData.employment_details.date_of_joining}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-2 self-start sm:self-auto">
              <button
                onClick={fetchEmployeeData}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                title="Refresh data"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Sections */}
      <div className="space-y-8">
        {/* Personal Information */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 mb-6">
            <User className="h-6 w-6 text-blue-600" />
            <h3 className="text-xl font-semibold text-gray-900">Personal Information</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* <InputField
              label="Employee Name"
              value={toTitleCase(employeeData.personal_information.employee_name)}
              onChange={(value) => handleInputChange('personal_information.employee_name', value)}
              required
            /> */}
            <InputField 
              label="First Name"
              value={toTitleCase(employeeData.firstName)}
              onChange={(value) => handleInputChange('firstName', value)}
              disabled={user.right === 'EMPLOYEE'}
            />
            <InputField 
              label="Middle Name"
              value={toTitleCase(employeeData.middleName)}
              onChange={(value) => handleInputChange('middleName', value)}
              disabled={user.right === 'EMPLOYEE'}
            />
            <InputField
              label="Last Name"
              value={toTitleCase(employeeData.lastName)}
              onChange={(value) => handleInputChange('lastName', value)}
              disabled={user.right === 'EMPLOYEE'}
            />
            <InputField
              label="Father's Name"
              value={toTitleCase(employeeData.personal_information.father_name)}
              onChange={(value) => handleInputChange('personal_information.father_name', value)}
            />
            <InputField
              label="Mother's Name"
              value={toTitleCase(employeeData.personal_information.mother_name)}
              onChange={(value) => handleInputChange('personal_information.mother_name', value)}
            />
            <InputField
              label="Date of Birth"
              type="date"
              value={employeeData.personal_information.date_of_birth}
              onChange={(value) => handleInputChange('personal_information.date_of_birth', value)}
            />
            <InputField
              label="Place of Birth"
              value={toTitleCase(employeeData.personal_information.place_of_birth)}
              onChange={(value) => handleInputChange('personal_information.place_of_birth', value)}
            />
            <SelectField
              label="Gender"
              value={toTitleCase(employeeData.personal_information.gender)}
              onChange={(value) => handleInputChange('personal_information.gender', value)}
              options={['Male', 'Female', 'Other']}
            />
            <InputField
              label="Nationality"
              value={employeeData.personal_information.nationality}
              onChange={(value) => handleInputChange('personal_information.nationality', value)}
            />
            <InputField
              label="Religion"
              value={employeeData.personal_information.religion}
              onChange={(value) => handleInputChange('personal_information.religion', value)}
            />
            <SelectField
              label="Marital Status"
              value={toTitleCase(employeeData.personal_information.marital_status)}
              onChange={(value) => handleInputChange('personal_information.marital_status', value)}
              options={['Single', 'Married', 'Divorced', 'Widowed']}
            />
            <InputField
              label="Blood Group"
              value={toTitleCase(employeeData.personal_information.blood_group)}
              onChange={(value) => handleInputChange('personal_information.blood_group', value)}
            />
          </div>
          
          {/* Languages Known */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Languages Known</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {employeeData.personal_information.languages_known.map((lang, index) => (
                <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  {toTitleCase(lang)}
                  <button
                    onClick={() => removeLanguage(index)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newLanguage}
                onChange={(e) => setNewLanguage(e.target.value)}
                placeholder="Add language"
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && addLanguage()}
              />
              <button
                onClick={addLanguage}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="h-6 w-6 text-green-600" />
            <h3 className="text-xl font-semibold text-gray-900">Address Information</h3>
          </div>
          <label className="block text-md font-bold text-gray-700 mb-3">Permanent Address:----------</label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <InputField
              label="Village"
              value={toTitleCase(employeeData.address_information.permanent_address.village)}
              onChange={(value) => handleInputChange('address_information.permanent_address.village', value)}
            />
            <InputField
              label="Post"
              value={toTitleCase(employeeData.address_information.permanent_address.post)}
              onChange={(value) => handleInputChange('address_information.permanent_address.post', value)}
            />
            <InputField
              label="District"
              value={toTitleCase(employeeData.address_information.permanent_address.district)}
              onChange={(value) => handleInputChange('address_information.permanent_address.district', value)}
            />
            <InputField
              label="State"
              value={toTitleCase(employeeData.address_information.permanent_address.state)}
              onChange={(value) => handleInputChange('address_information.permanent_address.state', value)}
            />
            <InputField
              label="PIN Code"
              value={employeeData.address_information.permanent_address.pin_code}
              onChange={(value) => handleInputChange('address_information.permanent_address.pin_code', value)}
            />
          </div>
          <label className="block text-md font-bold text-gray-700 mb-3 mt-6">Present Address:----------</label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <InputField
              label="Village"
              value={toTitleCase(employeeData.address_information.current_address?.village)}
              onChange={(value) => handleInputChange('address_information.current_address.village', value)}
            />
            <InputField
              label="Post"
              value={toTitleCase(employeeData.address_information.current_address?.post)}
              onChange={(value) => handleInputChange('address_information.current_address.post', value)}
            />
            <InputField
              label="District"
              value={toTitleCase(employeeData.address_information.current_address?.district)}
              onChange={(value) => handleInputChange('address_information.current_address.district', value)}
            />
            <InputField
              label="State"
              value={toTitleCase(employeeData.address_information.current_address?.state)}
              onChange={(value) => handleInputChange('address_information.current_address.state', value)}
            />
            <InputField
              label="PIN Code"
              value={employeeData.address_information.current_address?.pin_code}
              onChange={(value) => handleInputChange('address_information.current_address.pin_code', value)}
            />
          </div>

        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 mb-6">
            <Phone className="h-6 w-6 text-purple-600" />
            <h3 className="text-xl font-semibold text-gray-900">Contact Information</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <InputField
              label="Phone Number"
              value={employeeData.contact_information.phone_number}
              onChange={(value) => handleInputChange('contact_information.phone_number', value)}
            />
            <InputField
              label="Email"
              type="email"
              value={employeeData.contact_information.email}
              onChange={(value) => handleInputChange('contact_information.email', value)}
            />
          </div>
          
          <h4 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField
              label="Emergency Contact Name"
              value={toTitleCase(employeeData.contact_information.emergency_contact.name)}
              onChange={(value) => handleInputChange('contact_information.emergency_contact.name', value)}
            />
            <InputField
              label="Emergency Contact Mobile"
              value={employeeData.contact_information.emergency_contact.mobile}
              onChange={(value) => handleInputChange('contact_information.emergency_contact.mobile', value)}
            />
            <InputField
              label="Emergency Contact Email"
              type="email"
              value={employeeData.contact_information.emergency_contact.email}
              onChange={(value) => handleInputChange('contact_information.emergency_contact.email', value)}
            />
          </div>
        </div>

        {/* Identification Documents */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 mb-6">
            <CreditCard className="h-6 w-6 text-orange-600" />
            <h3 className="text-xl font-semibold text-gray-900">Identification Documents</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField
              label="Aadhaar Number"
              value={employeeData.identification_documents.aadhaar_number}
              onChange={(value) => handleInputChange('identification_documents.aadhaar_number', value)}
            />
            <InputField
              label="PAN Number"
              value={employeeData.identification_documents.pan_number}
              onChange={(value) => handleInputChange('identification_documents.pan_number', value)}
            />
          </div>
        </div>

        {/* Employment Details */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 mb-6">
            <Building className="h-6 w-6 text-indigo-600" />
            <h3 className="text-xl font-semibold text-gray-900">Employment Details</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <InputField
              label="Designation"
              value={toTitleCase(employeeData.employment_details.designation)}
              onChange={(value) => handleInputChange('employment_details.designation', value)}
            />
            <InputField
              label="Grade"
              value={toTitleCase(employeeData.employment_details.grade)}
              onChange={(value) => handleInputChange('employment_details.grade', value)}
            />
            <InputField
              label="UAN Number"
              value={employeeData.employment_details.uan_number}
              onChange={(value) => handleInputChange('employment_details.uan_number', value)}
            />
            <InputField
              label="Date of Joining"
              type="date"
              value={employeeData.employment_details.date_of_joining}
              onChange={(value) => handleInputChange('employment_details.date_of_joining', value)}
            />
            <InputField
              label="Date of Appointment"
              type="date"
              value={employeeData.employment_details.date_of_appointment}
              onChange={(value) => handleInputChange('employment_details.date_of_appointment', value)}
            />
            <InputField
              label="Location"
              value={toTitleCase(employeeData.employment_details.location)}
              onChange={(value) => handleInputChange('employment_details.location', value)}
            />
            <InputField
              label="Division"
              value={toTitleCase(employeeData.employment_details.division)}
              onChange={(value) => handleInputChange('employment_details.division', value)}
            />
            <InputField
              label="Reporting Manager"
              value={toTitleCase(employeeData.employment_details.reporting_manager)}
              onChange={(value) => handleInputChange('employment_details.reporting_manager', value)}
            />
            <InputField
              label="Source of Candidate"
              value={toTitleCase(employeeData.employment_details.source_of_candidate)}
              onChange={(value) => handleInputChange('employment_details.source_of_candidate', value)}
            />
            <InputField
              label="Last Promotion Date"
              type="date"
              value={employeeData.employment_details.last_promotion_date}
              onChange={(value) => handleInputChange('employment_details.last_promotion_date', value)}
            />
            <InputField
              label="Official Email"
              value={employeeData.employment_details.official_email}
              onChange={(value) => handleInputChange('employment_details.official_email', value)}
            />
          </div>
        </div>

        {/* Salary and Compensation */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 mb-6">
            <DollarSign className="h-6 w-6 text-green-600" />
            <h3 className="text-xl font-semibold text-gray-900">Salary and Compensation</h3>
          </div>
          {
            Object.keys(employeeData.salary_and_compensation).map((key, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <InputField
                  label={key+" CTC"}
                  type="number"
                  value={employeeData.salary_and_compensation[key].ctc}
                  onChange={(value) => handleInputChange(`salary_and_compensation.${key}.ctc`, parseFloat(value) || 0)}
                />
                <InputField
                  label={key+" Basic"}
                  type="number"
                  value={employeeData.salary_and_compensation[key].basic}
                  onChange={(value) => handleInputChange(`salary_and_compensation.${key}.basic`, parseFloat(value) || 0)}
                />
                <InputField
                  label={key+" Gross"}
                  type="number"
                  value={employeeData.salary_and_compensation[key].gross}
                  onChange={(value) => handleInputChange(`salary_and_compensation.${key}.gross`, parseFloat(value) || 0)}
                />
                <InputField
                  label="Incentive"
                  type="number"
                  value={employeeData.salary_and_compensation[key].incentive}
                  onChange={(value) => handleInputChange(`salary_and_compensation.${key}.incentive`, parseFloat(value) || 0)}
                />
              </div>
            ))
          }
        </div>

        {/* Bank Details */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 mb-6">
            <CreditCard className="h-6 w-6 text-blue-600" />
            <h3 className="text-xl font-semibold text-gray-900">Bank Details</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField
              label="Bank Name"
              value={employeeData.bank_details.bank_name}
              onChange={(value) => handleInputChange('bank_details.bank_name', value)}
            />
            <InputField
              label="Account Number"
              value={employeeData.bank_details.account_number}
              onChange={(value) => handleInputChange('bank_details.account_number', value)}
            />
            <InputField
              label="IFSC Code"
              value={employeeData.bank_details.ifsc_code}
              onChange={(value) => handleInputChange('bank_details.ifsc_code', value)}
            />
          </div>
        </div>

        {/* Nominee Information */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Users className="h-6 w-6 text-purple-600" />
              <h3 className="text-xl font-semibold text-gray-900">Nominee Information</h3>
            </div>
            <button
              onClick={addNominee}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
            >
              Add Nominee
            </button>
          </div>
          
          {employeeData.nominee_information.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No nominees added yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              {employeeData.nominee_information.map((nominee, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-gray-900">Nominee {index + 1}</h4>
                    <button
                      onClick={() => removeNominee(index)}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <InputField
                      label="Nominee Name"
                      value={nominee.nominee_name}
                      onChange={(value) => handleNomineeChange(index, 'nominee_name', value)}
                    />
                    <InputField
                      label="Relationship"
                      value={nominee.relationship}
                      onChange={(value) => handleNomineeChange(index, 'relationship', value)}
                    />
                    <InputField
                      label="Age"
                      type="number"
                      value={nominee.nominee_age}
                      onChange={(value) => handleNomineeChange(index, 'nominee_age', parseInt(value) || 0)}
                    />
                    <InputField
                      label="Date of Birth"
                      type="date"
                      value={nominee.nominee_dob}
                      onChange={(value) => handleNomineeChange(index, 'nominee_dob', value)}
                    />
                    <InputField
                      label="Share Percentage"
                      type="number"
                      value={nominee.share_percentage}
                      onChange={(value) => handleNomineeChange(index, 'share_percentage', parseFloat(value) || 0)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Education */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 mb-6">
            <GraduationCap className="h-6 w-6 text-indigo-600" />
            <h3 className="text-xl font-semibold text-gray-900">Education</h3>
          </div>
          <div className="grid grid-cols-1 gap-6">
            {
              Object.keys(employeeData.education).map((key)=>{
                if(key === "other") return null;
                return(
                  <div className="grid grid-cols-2 gap-6" key={key}>
                  <InputField
                    label={key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())+ " Degree"}
                    value={employeeData.education[key].degree}
                    onChange={(value) => handleInputChange(`education.${key}.degree`, value)}
                  />
                  <InputField
                    label={key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())+ " Institution"}
                    value={employeeData.education[key].education_source}
                    onChange={(value) => handleInputChange(`education.${key}.education_source`, value)}
                  />
                </div>
                )
              })
            }
          </div>
        </div>

        {/* Declarations and Compliance */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="h-6 w-6 text-red-600" />
            <h3 className="text-xl font-semibold text-gray-900">Declarations and Compliance</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SelectField
              label="Physical Disabilities"
              value={employeeData.declarations_and_compliance.physical_disabilities}
              onChange={(value) => handleInputChange('declarations_and_compliance.physical_disabilities', value)}
              options={['Yes', 'No']}
            />
            <SelectField
              label="Major Illness"
              value={employeeData.declarations_and_compliance.major_illness}
              onChange={(value) => handleInputChange('declarations_and_compliance.major_illness', value)}
              options={['Yes', 'No']}
            />
            <SelectField
              label="Court Cases"
              value={employeeData.declarations_and_compliance.court_cases}
              onChange={(value) => handleInputChange('declarations_and_compliance.court_cases', value)}
              options={['Yes', 'No']}
            />
            <SelectField
              label="Police Custody"
              value={employeeData.declarations_and_compliance.police_custody}
              onChange={(value) => handleInputChange('declarations_and_compliance.police_custody', value)}
              options={['Yes', 'No']}
            />
          </div>
        </div>

        {/* Tick for confirm this deatils as verified */}
        <div className="flex items-center align-center w-full gap-3 mb-6">
          <div className="flex items-center align-center gap-2 ml-5">
            <input type="checkbox" checked={employeeData.isVerified} onChange={(e) => handleInputChange('isVerified', e.target.checked)} />
            {employeeData.isVerified ? <Check className="h-6 w-6 text-green-600" /> : <X className="h-6 w-6 text-red-600" />}
          </div>
          <h3 className="text-xl font-semibold text-gray-900">Verified by HR Department</h3>
        </div>

        {/* Tick for the active status */}
        <div className="flex items-center align-center w-full gap-3 mb-6">
          <div className="flex items-center align-center gap-2 ml-5">
            <input type="checkbox" checked={employeeData.isActive} onChange={(e) => handleInputChange('isActive', e.target.checked)} />
            {employeeData.isActive ? <Check className="h-6 w-6 text-green-600" /> : <X className="h-6 w-6 text-red-600" />}
          </div>
          <h3 className="text-xl font-semibold text-gray-900">Active Status</h3>
        </div>

        {/* if inactive then open inactive info group to take these four fields  last_working_day, resignation_date, reson_for leaving and remarks and it's gourp name should be inactive_info */}
        {
          !employeeData.isActive&&(
            <>
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center gap-3 mb-6">
                <GraduationCap className="h-6 w-6 text-indigo-600" />
                <h3 className="text-xl font-semibold text-gray-900">Inactive Information</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <InputField
                  label="Last Working Day"
                  type="date"
                  value={employeeData.inactive_info?.last_working_day}
                  onChange={(value) => handleInputChange('inactive_info.last_working_day', value)}
                />
                <InputField
                  label="Resignation Date"
                  type="date"
                  value={employeeData.inactive_info?.resignation_date}
                  onChange={(value) => handleInputChange('inactive_info.resignation_date', value)}
                />
                <InputField
                  label="Reason for Leaving"
                  value={employeeData.inactive_info?.reason_for_leaving}
                  onChange={(value) => handleInputChange('inactive_info.reason_for_leaving', value)}
                />
                <InputField
                  label="Remarks"
                  value={employeeData.inactive_info?.remarks}
                  onChange={(value) => handleInputChange('inactive_info.remarks', value)}
                />
              </div>
            </div>
            
            </>
            
          )
        }
      </div>

      {/* Fixed Save Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          {saving ? (
            <RefreshCw className="h-5 w-5 animate-spin" />
          ) : (
            <Save className="h-5 w-5" />
          )}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Success/Error Messages */}
      {saving && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded-lg shadow-lg">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span>Saving changes...</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeEditPage;

const InputField = ({ label, value, onChange, type = "text", placeholder = "", required = false, disabled = false }) => (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        disabled={disabled}
      />
    </div>
  );